import React, { useState, useEffect, useRef, useMemo } from "react";
import { AppAlert } from "../../common/appAlert/AppAlert";
import PropTypes from "prop-types";
import Grid from "@mui/material/Unstable_Grid2";
import { Paper, Typography, Box, Button, Alert } from "@mui/material";
import { DataTable } from "../../common/dataTable/DataTable";
import { initLocalization } from "../../../utils/helpers";
import { BisListingMappedStrings } from "./locale";
import { Remove as RemoveIcon } from "@mui/icons-material";
import useGetMappedBisList from "../../../hooks/media/useGetMappedBisList";
import useDissociateMapping from "../../../hooks/media/useDissociateMapping";

export const BisListingMapped = ({
  isLoading = false,
  selectedMedia,
  refreshKey,
  onRefreshNotMapped,
}) => {
  initLocalization(BisListingMappedStrings);

  const [selectedRows, setSelectedRows] = useState(() => new Set());

  const {
    mediaMappingList: mappedBis,
    isLoading: isMappedLoading,
    getMappedBisList,
  } = useGetMappedBisList();
  useEffect(() => {
    if (selectedMedia?.mediaId) {
      getMappedBisList({
        mediaId: selectedMedia.mediaId,
        dataProviderId: selectedMedia.dataProviderId,
        displayTypeId: selectedMedia.displayTypeId,
        includeMapped: true,
      });
    }
  }, [selectedMedia, refreshKey]);
  const [localError, setLocalError] = useState("");
  const {
    isLoading: isDissociating,
    error,
    dissociateMapping,
  } = useDissociateMapping();

  useEffect(() => {
    if (error) {
      setLocalError(error);
    }
  }, [error]);

  const indexToId = useRef({});
  const rows = useMemo(() => {
    const newIndexToId = {};
    const result = (mappedBis || []).map((item, index) => {
      const rowId = item.id || index;
      newIndexToId[index] = rowId;
      return {
        id: rowId,
        IdBIS: item.publisherId || "-",
        LabelBIS: item.ytPublisherChannelName || item.publisherDomain || "-",
      };
    });
    indexToId.current = newIndexToId;
    return result;
  }, [mappedBis]);

  const columns = [
    { field: "IdBIS", headerName: BisListingMappedStrings.IdBIS, minWidth: 30 },
    {
      field: "LabelBIS",
      headerName: BisListingMappedStrings.LabelBIS,
      minWidth: 50,
    },
  ];

  const handleSelectRow = (event, index) => {
    const rowId = indexToId.current[index];
    if (!rowId) {
      console.warn("Impossible de récupérer rowId à partir de l'index", index);
      return;
    }

    const updatedSelectedRows = new Set(selectedRows);

    if (updatedSelectedRows.has(rowId)) {
      updatedSelectedRows.delete(rowId);
    } else {
      updatedSelectedRows.add(rowId);
    }

    setSelectedRows(updatedSelectedRows);
  };

  const handleDissociate = () => {
    const idsToDelete = Array.from(selectedRows);
    if (selectedMedia?.mediaId && idsToDelete.length > 0) {
      dissociateMapping(selectedMedia.mediaId, idsToDelete, () => {
        getMappedBisList({
          mediaId: selectedMedia.mediaId,
          dataProviderId: selectedMedia.dataProviderId,
          displayTypeId: selectedMedia.displayTypeId,
          includeMapped: true,
        });

        setSelectedRows(new Set());

        if (onRefreshNotMapped) {
          onRefreshNotMapped();
        }
      });
    }
  };

  return (
    <Paper sx={{ padding: "0.5rem", height: "100%", position: "relative" }}>
      <Grid container direction="column" height="100%">
        <Grid xs={12} paddingBottom={2} paddingTop={1} paddingLeft={1}>
          <Typography data-testid="mapped-bis-title">
            {BisListingMappedStrings.title}
          </Typography>
        </Grid>

        <Grid xs={12} display="flex" gap={2} paddingX={1} paddingBottom={2}>
          <Box flex={1}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "bold", fontSize: "1.1rem" }}
            >
              {BisListingMappedStrings.MediaLabel}
            </Typography>
            <Typography variant="body1">
              {selectedMedia?.mediaName ?? ""}
            </Typography>
          </Box>
          <Box flex={1}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "bold", fontSize: "1.1rem" }}
            >
              {BisListingMappedStrings.MediaId}
            </Typography>
            <Typography variant="body1">
              {selectedMedia?.mediaId ?? ""}
            </Typography>
          </Box>
          <Box flex={1}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "bold", fontSize: "1.1rem" }}
            >
              {BisListingMappedStrings.DisplayMediaType}
            </Typography>
            <Typography variant="body1">
              {selectedMedia?.displayTypeName ?? ""}
            </Typography>
          </Box>
        </Grid>
        <Grid
          xs={12}
          display="flex"
          flexDirection="column"
          flexGrow={1}
          sx={{ overflow: "auto" }}
        >
          <DataTable
            selectedRows={[...selectedRows]}
            columns={columns}
            rows={rows}
            showPagination={false}
            allowSelection={true}
            showNoDataMessage={!isMappedLoading}
            tableMinWidth={750}
            onSelectRow={handleSelectRow}
          />
        </Grid>

        <Grid container justifyContent="flex-end" mt={2}>
          <Grid>
            <Button
              startIcon={<RemoveIcon />}
              variant="outlined"
              size="large"
              disabled={selectedRows.size === 0 || isDissociating}
              onClick={handleDissociate}
            >
              {isDissociating
                ? BisListingMappedStrings.dissociationInProgress
                : BisListingMappedStrings.dissocier}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        xs={12}
        paddingTop={3}
        sx={{ textAlign: "right", position: "relative", zIndex: 5 }}
      ></Grid>
      <AppAlert
        sx={{ mt: 2 }}
        key={localError}
        open={Boolean(localError)}
        type="error"
        onClose={() => setLocalError("")}
      >
        {localError}
      </AppAlert>
    </Paper>
  );
};

BisListingMapped.propTypes = {
  selectedMedia: PropTypes.object,
  isLoading: PropTypes.bool,
  refreshKey: PropTypes.number,

  onSelectedMedia: PropTypes.func,
};

export default BisListingMapped;
